import { get, has } from 'lodash-es';
import { isObject } from 'remeda';

declare global {
  interface Window {
    __STORYBOOK_PROJECT_TARGET__?: ProjectTarget;
  }
}

export const projectTargetMap = {
  mildang: true,
  // Seoul learn https://slearn.seoul.go.kr/
  slearn: true,
  laschool: true,
  afterSchool: true,
  schoolpt: {
    educore: true,
    gs: true,
    aidt: {
      retest: true,
      kaidt: true,
    },
    workshop: true,
    udl: true,
    sv: true,
  },
  hub: true,
} as const;

type Primitive = string | number | boolean | bigint | symbol | undefined | null;

type FlattenObjectKeys<T, Prefix extends string = ''> = T extends Primitive | Array<any>
  ? never
  : {
      [K in keyof T]-?: K extends string
        ? `${Prefix}${K}` | FlattenObjectKeys<T[K], `${Prefix}${K}.`>
        : never;
    }[keyof T];

export type ProjectTarget = FlattenObjectKeys<typeof projectTargetMap>;

const DEFAULT_PROJECT_TARGET: ProjectTarget = 'mildang';

export const getProjectTarget = (): ProjectTarget => {
  if (typeof window !== 'undefined' && window.__STORYBOOK_PROJECT_TARGET__) {
    return window.__STORYBOOK_PROJECT_TARGET__;
  }

  return (process.env.NEXT_PUBLIC_PROJECT_TARGET ?? DEFAULT_PROJECT_TARGET) as ProjectTarget;
};
// export const PROJECT_TARGET: ProjectTarget = getProjectTarget();

type WithDefault<S, T> = S & { _default?: T };

type ProjectTargetSelect<T> = WithDefault<
  {
    mildang?: T;
    slearn?: T;
    laschool?: T;
    afterSchool?: T;
    hub?: T;
    schoolpt?:
      | T
      | WithDefault<
        {
            educore?: T;
            gs?: T;
            aidt?: T | WithDefault<{ retest?: T; kaidt?: T }, T>;
            workshop?: T;
            udl?: T;
            sv?: T;
          },
          T
        >;
  },
  T
>;

export const DEFAULT_PROPERTY = '_default';

/**
 * Selects a value based on the current project target or defaults to the provided default value.
 * @param choices - The choices object which contains possible values for each project target.
 * @returns The value corresponding to the current `PR3OJECT_TARGET` or the default value.
 */
export function select<T>(choices: ProjectTargetSelect<T>): T {
  let targetValue = (has(choices, '_default') ? choices?._default : choices.mildang) as T;

  const currentProjectTargets = getProjectTarget().split('.');
  let currentChoice = choices;
  let currentProjectTargetMap = projectTargetMap;

  while (currentProjectTargets.length !== 0) {
    const targetProperty = currentProjectTargets.shift();

    if (!targetProperty) {
      break;
    }

    if (has(currentChoice, targetProperty)) {
      const choiceValue = get(currentChoice, targetProperty);

      if (!isObject(choiceValue)) {
        targetValue = choiceValue!;
        break;
      }

      const targetProperties = Object.keys((currentProjectTargetMap as Record<string, any>)[targetProperty]);
      const isAllTargetProperties = Object.keys(choiceValue)
        .filter((key) => key !== DEFAULT_PROPERTY)
        .every((key) => targetProperties.includes(key));

      if (isAllTargetProperties) {
        if (has(choiceValue, DEFAULT_PROPERTY)) {
          targetValue = choiceValue?.[DEFAULT_PROPERTY]!;
        }
      } else {
        targetValue = choiceValue as T;
      }
      currentChoice = choiceValue;
      currentProjectTargetMap = (currentProjectTargetMap as Record<string, any>)[targetProperty];
    }
  }

  return targetValue;
}

interface IsProjectOptions {
  exact?: boolean;
  excludes?: ProjectTarget[];
}

export function isProject(target: ProjectTarget, options?: IsProjectOptions): boolean {
  const projectTarget = getProjectTarget();
  const projectTargets = projectTarget.split('.');
  const targetParts = target.split('.');

  if (target !== projectTarget && options?.excludes?.includes(projectTarget)) {
    return false;
  }

  if (options?.exact) {
    return projectTarget === target;
  }

  const isPrefixMatch = targetParts.every((part, index) => part === projectTargets[index]);
  return isPrefixMatch;
}

export function isDomainAuthProject(): boolean {
  return isProject('slearn') || isProject('laschool');
}
